import { useRef, useEffect, useMemo, useReducer } from 'react';
import produce from 'immer';
export { castDraft, current, original, setAutoFreeze } from 'immer';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function isImmutable(value) {
  return typeof value !== 'object' || value === null || typeof value === 'undefined';
}

function _detectMutations(trackedProperty, obj, sameParentRef, path) {
  if (sameParentRef === void 0) {
    sameParentRef = false;
  }

  if (path === void 0) {
    path = [];
  }

  var prevObj = trackedProperty ? trackedProperty.value : undefined;
  var sameRef = prevObj === obj;

  if (sameParentRef && !sameRef && !Number.isNaN(obj)) {
    return {
      wasMutated: true,
      path: path
    };
  }

  if (isImmutable(prevObj) || isImmutable(obj)) {
    return {
      wasMutated: false
    };
  } // Gather all keys from prev (tracked) and after objs


  var keysToDetect = {};
  Object.keys(trackedProperty.children).forEach(function (key) {
    keysToDetect[key] = true;
  });
  Object.keys(obj).forEach(function (key) {
    keysToDetect[key] = true;
  });
  var keys = Object.keys(keysToDetect);

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var childPath = path.concat(key);

    var result = _detectMutations(trackedProperty.children[key], obj[key], sameRef, childPath);

    if (result.wasMutated) {
      return result;
    }
  }

  return {
    wasMutated: false
  };
}

function trackProperties(obj, path) {
  if (path === void 0) {
    path = [];
  }

  var tracked = {
    value: obj
  };

  if (!isImmutable(obj)) {
    tracked.children = {};

    for (var key in obj) {
      var childPath = path.concat(key);
      tracked.children[key] = trackProperties(obj[key], childPath);
    }
  }

  return tracked;
}

function trackForMutations(obj) {
  var trackedProperties = trackProperties(obj);
  return {
    detectMutations: function detectMutations() {
      return _detectMutations(trackedProperties, obj);
    }
  };
}
function getValueAtPath(obj, path) {
  if (path === void 0) {
    path = [];
  }

  try {
    return path.reduce(function (prev, curr) {
      if (!isImmutable(prev) && curr in prev) {
        return prev[curr];
      }

      return prev;
    }, obj);
  } catch (err) {
    return '(Unable to retrieve value from path)';
  }
}

var MutationError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(MutationError, _Error);

  function MutationError(message) {
    var _this;

    _this = _Error.call(this, message) || this;
    _this.name = 'MutationError';
    _this.message = message;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(_assertThisInitialized(_this), MutationError);
    }

    return _this;
  }

  return MutationError;
}( /*#__PURE__*/_wrapNativeSuper(Error));
function useTrackMutations(obj, objectName) {
  var mutationTracker = useRef(trackForMutations(obj));
  useEffect(function () {
    var result = mutationTracker.current.detectMutations();

    if (result.wasMutated) {
      var msgObjName = objectName || 'provided';
      var valueAtPath = getValueAtPath(obj, result.path);
      var message = "A state mutation was detected in the " + msgObjName + " object at path: " + (result.path || []).join('.') + ". Value: " + valueAtPath;
      throw new MutationError(message);
    }

    mutationTracker.current = trackForMutations(obj);
  });
}

function isStepValid(step, historyLength) {
  if (typeof step !== 'number') {
    console.error("Please only pass a number to this function! Received '" + step + "', type: " + typeof step + ".");
    return false;
  }

  if (step < 0) {
    console.error("Step index " + step + " below bounds!");
    return false;
  }

  if (step >= historyLength) {
    console.error("Step index " + step + " above bounds! History length is " + historyLength);
    return false;
  }

  return true;
}

/*=============================/
/        Reducer creator       /
/=============================*/

function makeReducer(initialState) {
  return function reducer(state, action) {
    switch (action.type) {
      case 'state/setState':
        {
          return setStateReducer(state, action);
        }

      case 'state/goTo':
        {
          return goToReducer(state, action);
        }

      case 'state/goBack':
        {
          return goBackReducer(state);
        }

      case 'state/goForward':
        {
          return goForwardReducer(state);
        }

      case 'state/saveCheckpoint':
        {
          return saveCheckpointReducer(state);
        }

      case 'state/restoreCheckpoint':
        {
          return restoreCheckpointReducer(state);
        }

      case 'state/reset':
        {
          return initialState;
        }

      default:
        {
          // @ts-expect-error shouldn't ever reach this branch, type should be never
          throw new Error("Received an unexpected action type: " + action.type);
        }
    }
  };
}
/*=============================/
/           Reducers           /
/=============================*/

/**
 * Reducer for setting next state
 */

function setStateReducer(state, action) {
  var nextStatePiece;
  var _action$payload = action.payload,
      updates = _action$payload.updates,
      includeInHistory = _action$payload.includeInHistory;

  if (typeof updates === 'function') {
    var updater = updates; // get new state piece

    nextStatePiece = produce(state.currentStateItem, updater);
  } else {
    nextStatePiece = updates;
  }

  if (nextStatePiece === state.currentStateItem) {
    return state;
  } // chop off any 'future' history if applicable


  var choppedHistory;
  var newHistory;
  var newStepNum;

  if (includeInHistory) {
    choppedHistory = state.history.slice(0, state.stepNum + 1);
    newHistory = [].concat(choppedHistory, [nextStatePiece]);
    newStepNum = state.stepNum + 1;
  } else {
    choppedHistory = state.history;
    newHistory = state.history;
    newStepNum = state.stepNum;
  } // checkpoint is invalid here if it was already invalid
  // also invalid if it is equal to the last index that is being overwritten


  var isCheckpointStillValid = state.isCheckpointValid && state.checkpoint < choppedHistory.length;
  return _extends({}, state, {
    currentStateItem: nextStatePiece,
    isPartOfHistory: includeInHistory,
    history: newHistory,
    stepNum: newStepNum,
    isCheckpointValid: isCheckpointStillValid
  });
}
/**
 * Reducer for going to a particular step
 */


function goToReducer(state, action) {
  var stepNum = action.payload;

  if (isStepValid(stepNum, state.history.length)) {
    return _extends({}, state, {
      stepNum: stepNum,
      currentStateItem: state.history[stepNum],
      isPartOfHistory: true
    });
  }

  return state;
}
/**
 * Reducer for going back one step
 */


function goBackReducer(state) {
  var previousStep = state.stepNum - 1;

  if (isStepValid(previousStep, state.history.length)) {
    return _extends({}, state, {
      stepNum: previousStep,
      currentStateItem: state.history[previousStep],
      isPartOfHistory: true
    });
  }

  return state;
}
/**
 * Reducer for going forward one step
 */


function goForwardReducer(state) {
  var nextStep = state.stepNum + 1;

  if (isStepValid(nextStep, state.history.length)) {
    return _extends({}, state, {
      stepNum: nextStep,
      currentStateItem: state.history[nextStep],
      isPartOfHistory: true
    });
  }

  return state;
}
/**
 * Reducer for saving current step as a 'checkpoint'
 */


function saveCheckpointReducer(state) {
  return _extends({}, state, {
    checkpoint: state.stepNum,
    isCheckpointValid: true
  });
}
/**
 * Reducer for going directly to the saved 'checkpoint'
 */


function restoreCheckpointReducer(state) {
  var checkpoint = state.checkpoint,
      isCheckpointValid = state.isCheckpointValid;

  if (!isCheckpointValid) {
    console.error("Unable to restore checkpoint: saved checkpoint at index " + checkpoint + " no longer exists!");
    return state;
  } // *should* always be valid here, but check as a precaution


  if (isStepValid(checkpoint, state.history.length)) {
    return _extends({}, state, {
      stepNum: checkpoint,
      currentStateItem: state.history[checkpoint],
      isPartOfHistory: true
    });
  }

  return state;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */

function useImmerState(initialState) {
  // initial state placed in a ref (and never changed)
  // so we can use it without lying to dependency arrays
  var initialStateRef = useRef(initialState);
  /*=============================/
  /     Initial Reducer State    /
  /=============================*/

  var initialReducerState = useMemo(function () {
    var passedInInitialState = initialStateRef.current;
    var initialStatePiece = passedInInitialState; // handle lazy initial state if applicable

    if (typeof passedInInitialState === 'function') {
      var lazyInitialState = passedInInitialState;
      initialStatePiece = lazyInitialState();
    } else {
      initialStatePiece = passedInInitialState;
    }

    return {
      currentStateItem: initialStatePiece,
      isPartOfHistory: true,
      history: [initialStatePiece],
      stepNum: 0,
      checkpoint: 0,
      isCheckpointValid: true
    };
  }, []);
  /*=============================/
  /           Reducer            /
  /=============================*/

  var reducer = useMemo(function () {
    return makeReducer(initialReducerState);
  }, [initialReducerState]);
  /*=============================/
  /          useReducer          /
  /=============================*/

  var _React$useReducer = useReducer(reducer, initialReducerState),
      state = _React$useReducer[0],
      dispatchAction = _React$useReducer[1];
  /*=============================/
  /      Mutation tracking       /
  /=============================*/


  if (process.env.NODE_ENV !== 'production') {
    // Yes we broke the rule, but kept the spirit.
    // The number of hooks won't change between renders,
    // because the environment won't change between renders.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTrackMutations(state.history, 'State History');
  }
  /*=============================/
  /         Exposed API          /
  /=============================*/


  var handlers = useMemo(function () {
    return {
      /**
       * Set new state. Accepts either a static value, or functional notation.
       * Using functional notation allows for writing draft updates 'mutably'
       * to produce the next immutable state.
       */
      setState: function setState(updates, includeInHistory) {
        if (includeInHistory === void 0) {
          includeInHistory = true;
        }

        dispatchAction({
          type: 'state/setState',
          payload: {
            updates: updates,
            includeInHistory: includeInHistory
          }
        });
      },

      /**
       * Go to a provided step number (index) in the state history.
       */
      goTo: function goTo(step) {
        dispatchAction({
          type: 'state/goTo',
          payload: step
        });
      },

      /**
       * Go to the previous step in state history.
       */
      goBack: function goBack() {
        dispatchAction({
          type: 'state/goBack'
        });
      },

      /**
       * Go to the next step in state history.
       */
      goForward: function goForward() {
        dispatchAction({
          type: 'state/goForward'
        });
      },

      /**
       * Save the current step in state history as a checkpoint.
       */
      saveCheckpoint: function saveCheckpoint() {
        dispatchAction({
          type: 'state/saveCheckpoint'
        });
      },

      /**
       * Restore the saved checkpoint step in state history.
       * Will restore to initial state if no checkpoint was explicitly saved.
       */
      restoreCheckpoint: function restoreCheckpoint() {
        dispatchAction({
          type: 'state/restoreCheckpoint'
        });
      },

      /**
       * Reset to initial state, including state history & checkpoint.
       */
      reset: function reset() {
        dispatchAction({
          type: 'state/reset'
        });
      }
    };
  }, [dispatchAction]);
  var extraApi = useMemo(function () {
    return {
      history: state.history,
      stepNum: state.stepNum,
      isFirstStep: state.stepNum === 0,
      isLastStep: state.stepNum === state.history.length - 1,
      goTo: handlers.goTo,
      goBack: handlers.goBack,
      goForward: handlers.goForward,
      saveCheckpoint: handlers.saveCheckpoint,
      restoreCheckpoint: handlers.restoreCheckpoint,
      checkpoint: state.checkpoint,
      isCheckpointValid: state.isCheckpointValid,
      reset: handlers.reset
    };
  }, [state.history, state.stepNum, state.checkpoint, state.isCheckpointValid, handlers]);
  return [state.currentStateItem, handlers.setState, extraApi];
}

export { useImmerState };
//# sourceMappingURL=use-immer-state.esm.js.map
